import {
  type AsyncResult,
  createNotRequestedAsyncResult,
  createPendingAsyncResult,
  createResolvedAsyncResult,
} from "@/cache/AsyncResult";
import {
  type CacheDatum,
  CacheDatumState,
  type LoadedCacheDatum,
  type PermanentlyFailedCacheDatum,
} from "@/cache/CacheDatumState";
import {
  type BBox,
  WFS_TILE_ZOOM,
  bufferBBox,
  buildGeoJsonAggregateCacheId,
  buildGeoJsonTileCacheId,
  buildWfsTileCacheId,
  getGeoJsonTileConfig,
  getGeoJsonTilesForViewport,
  getWfsTileWindow,
  jetStreamsDedupKey,
  mergeFeatureCollectionsDedup,
  stableQueryKey,
  thunderstormDedupKey,
} from "@/cache/GeoJSONCache/tileUtils";
import { generateGenericCacheId, mergeFeatureCollections } from "@/cache/GeoJSONCache/utils";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { FailureState, type RetryState, createRetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import { type Stats, defaultCacheFormatter, jsxTable } from "@/cache/Stats";
import { createLoadedCacheDatum, sumLoadedCacheMemoryConsumption } from "@/cache/utils/cacheMemory";
import {
  createBarbsGeoJson,
  createRawGeoJson,
  createWeatherCodeGeoJson,
  createWeatherSymbolGeoJson,
  emptyFeatureCollection,
} from "@/geojson";
import {
  canonicalizeLightningRequestWindow,
  generateGridLayerCacheId,
  generateIsoLinesLayerCacheId,
  generateLightningListLayerCacheId,
} from "@/layers/geojson/LayerUtils";
import { type LayerKind, LayerKindName } from "@/layers/utility/createLayerObject";
import { recordLayerFetchMs } from "@/otel/metrics";
import { getProxyUrl } from "@/reducer/api/helper";
import { MiB } from "@/utility/bytesize";
import {
  Area,
  CoordinateSystem,
  GenericGeoJsonRequest_toGridRequest,
  type GeoJSONFeatureCollection,
  type GridRequest,
  type IsoLinesRequest,
  type JSONResponseBody,
  LIGHTNING_LIST_PATH,
  type LightningListRequest,
  MeteomaticsApiUrl,
  WORLD_BBOX,
  type WfsRequest,
  intersectWeatherModelBoundingBox,
} from "@mm/api.meteomatics.com";
import type { MeteomaticsApiError } from "@mm/api.meteomatics.com";
import { type Abort, isAbortable } from "@mm/api.meteomatics.com";
import type { GenericGeoJsonRequest } from "@mm/api.meteomatics.com";
import { DateTime, Duration } from "luxon";
import { cancelable } from "../utils";
import { evictCacheEntriesBySize, markCacheDatumUsed } from "../utils/cacheEviction";

export interface GeoJSONCacheStats {
  count: number;
  pending: number;
  estimatedMemoryConsumption: number;
}

export enum GridRequestKind {
  Raw = "raw",
  Symbol = "symbol",
  WeatherCode = "weather code",
  Barbs = "barbs",
}

export enum IsoLinesRequestKind {
  IsoLines = "isolines",
  IsoBands = "isobands",
  HighsAndLows = "highsandlows",
}

export enum PoiParameterRequestKind {
  SingleParameterColoring = "single parameter coloring",
}

export enum WfsRequestKind {
  Station = "station",
}

// Tile bbox buffer (0 disables). Increase if server clips geometries at tile edges.
const GEOJSON_TILE_BUFFER_RATIO = 0;
const DEFAULT_GEOJSON_CACHE_MAX_SIZE = MiB(128);
const DEFAULT_GEOJSON_CACHE_DELAY_EVICTION_SIZE = MiB(160);
// We cannot read auto-refresh state/rate in this cache layer, so live updates are TTL-driven.
const LIGHTNING_LIVE_BUCKET_TTL_MS = 60 * 1000;
const LIGHTNING_LIVE_OFFSET = Duration.fromObject({ minutes: 10 });
const LIGHTNING_MAX_FUTURE = Duration.fromObject({ hours: 3 });

type PendingGeoJsonOwner = string;

type PendingGeoJsonMeta = {
  area?: Area<CoordinateSystem.WGS84>;
  kind?: string;
  owners: Set<PendingGeoJsonOwner>;
  path?: string;
};

type PendingGeoJsonFilter = {
  kind?: string;
  owner?: PendingGeoJsonOwner;
  path?: string;
};

type CancelSupersededPendingGeoJsonFilter = PendingGeoJsonFilter & {
  keepPromise?: Promise<GeoJSONFeatureCollection>;
  owner: PendingGeoJsonOwner;
};

type PendingGeoJsonRequestOptions = {
  owner?: PendingGeoJsonOwner;
};

const toLightningApiAlignedRequest = (
  request: LightningListRequest<CoordinateSystem.WGS84>,
): LightningListRequest<CoordinateSystem.WGS84> => {
  // get_lightning_list interprets `datetime + duration` as [start, start + duration].
  // The layer request datetime represents the selected end time, so shift to start here.
  return {
    ...request,
    datetime: request.datetime.minus(request.timeDuration),
  };
};

export class GeoJSONCache implements Stats<GeoJSONCacheStats> {
  protected cache: { [key: string]: CacheDatum<GeoJSONFeatureCollection, RetryState> } = {};
  private pendingGeoJsonMeta = new Map<string, PendingGeoJsonMeta>();
  // Tracks canonical lightning requests per cache key so we can reuse supersets
  // and apply live-window TTL rules without re-parsing URLs.
  private lightningRequestMeta = new Map<string, LightningListRequest<CoordinateSystem.WGS84>>();

  constructor(
    private readonly label_: string,
    private readonly maxSize: number = DEFAULT_GEOJSON_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilSize: number = DEFAULT_GEOJSON_CACHE_DELAY_EVICTION_SIZE,
  ) {}

  stats(): GeoJSONCacheStats {
    let count = 0;
    let pending = 0;
    const cache = this.cache;

    for (const line in cache) {
      if (Object.hasOwn(cache, line)) {
        count++;

        if (cache[line].state === CacheDatumState.Pending) {
          pending++;
        }
      }
    }

    return {
      count,
      pending,
      estimatedMemoryConsumption: sumLoadedCacheMemoryConsumption(cache),
    };
  }

  descJsx(): JSX.Element {
    return <>Special-purpose cache for API JSON requests. Replacement policy is approximate memory-based LRU.</>;
  }

  label(): string {
    return this.label_;
  }

  clear() {
    const preservedKeys = Object.keys(this.cache).filter((key) => this.shouldPreserveCacheEntryOnClear(key));
    const newCache: typeof this.cache = {};
    const newLightningRequestMeta = new Map<string, LightningListRequest<CoordinateSystem.WGS84>>();
    for (const key of preservedKeys) {
      newCache[key] = this.cache[key];
      const lightningRequest = this.lightningRequestMeta.get(key);
      if (lightningRequest) {
        newLightningRequestMeta.set(key, lightningRequest);
      }
    }

    this.cache = newCache;
    this.pendingGeoJsonMeta.clear();
    this.lightningRequestMeta = newLightningRequestMeta;
  }

  statsJsx(): JSX.Element {
    return jsxTable(this.stats(), defaultCacheFormatter);
  }

  cancelPendingGeoJsonRequests(filter: PendingGeoJsonFilter = {}): void {
    for (const [cacheId, meta] of this.pendingGeoJsonMeta.entries()) {
      if (!this.matchesPendingGeoJsonFilter(meta, filter)) {
        continue;
      }
      const entry = this.cache[cacheId];
      if (!entry || entry.state !== CacheDatumState.Pending) {
        this.pendingGeoJsonMeta.delete(cacheId);
        continue;
      }
      if (filter.owner) {
        if (!this.releasePendingGeoJsonOwner(cacheId, filter.owner)) {
          continue;
        }
      }
      entry.cancel?.();
    }
  }

  cancelPendingGeoJsonRequestsOutsideArea(
    area: Area<CoordinateSystem.WGS84>,
    filter: PendingGeoJsonFilter & { owner: PendingGeoJsonOwner },
  ): void {
    for (const [cacheId, meta] of this.pendingGeoJsonMeta.entries()) {
      if (!this.matchesPendingGeoJsonFilter(meta, filter)) {
        continue;
      }

      const entry = this.cache[cacheId];
      if (!entry || entry.state !== CacheDatumState.Pending) {
        this.pendingGeoJsonMeta.delete(cacheId);
        continue;
      }

      if (!meta.area || meta.area.intersect(area) !== null) {
        continue;
      }

      if (!this.releasePendingGeoJsonOwner(cacheId, filter.owner)) {
        continue;
      }

      entry.cancel?.();
    }
  }

  cancelSupersededPendingGeoJsonRequests(filter: CancelSupersededPendingGeoJsonFilter): number {
    let canceledCount = 0;
    for (const [cacheId, meta] of this.pendingGeoJsonMeta.entries()) {
      if (!this.matchesPendingGeoJsonFilter(meta, filter)) {
        continue;
      }

      const entry = this.cache[cacheId];
      if (!entry || entry.state !== CacheDatumState.Pending) {
        this.pendingGeoJsonMeta.delete(cacheId);
        continue;
      }

      if (filter.keepPromise && entry.promise === filter.keepPromise) {
        continue;
      }

      if (!this.releasePendingGeoJsonOwner(cacheId, filter.owner)) {
        continue;
      }

      entry.cancel?.();
      canceledCount++;
    }
    return canceledCount;
  }

  private matchesPendingGeoJsonFilter(meta: PendingGeoJsonMeta, filter: PendingGeoJsonFilter): boolean {
    if (filter.kind && meta.kind !== filter.kind) {
      return false;
    }
    if (filter.path && meta.path !== filter.path) {
      return false;
    }
    if (filter.owner && !meta.owners.has(filter.owner)) {
      return false;
    }
    return true;
  }

  private releasePendingGeoJsonOwner(cacheId: string, owner: PendingGeoJsonOwner): boolean {
    const meta = this.pendingGeoJsonMeta.get(cacheId);
    if (!meta) {
      return true;
    }

    meta.owners.delete(owner);
    if (meta.owners.size > 0) {
      return false;
    }

    this.pendingGeoJsonMeta.delete(cacheId);
    return true;
  }

  private registerPendingGeoJsonMeta(
    cacheId: string,
    meta: { area?: Area<CoordinateSystem.WGS84>; kind?: string; owner?: PendingGeoJsonOwner; path?: string },
  ) {
    const existing = this.pendingGeoJsonMeta.get(cacheId);
    if (existing) {
      if (meta.area) {
        existing.area = meta.area;
      }
      if (meta.kind) {
        existing.kind = meta.kind;
      }
      if (meta.path) {
        existing.path = meta.path;
      }
      if (meta.owner) {
        existing.owners.add(meta.owner);
      }
      return;
    }

    this.pendingGeoJsonMeta.set(cacheId, {
      area: meta.area,
      kind: meta.kind,
      owners: new Set(meta.owner ? [meta.owner] : []),
      path: meta.path,
    });
  }

  private clearPendingGeoJsonMeta(cacheId: string) {
    this.pendingGeoJsonMeta.delete(cacheId);
  }

  private markUsed(cacheLine: LoadedCacheDatum<GeoJSONFeatureCollection>) {
    markCacheDatumUsed(cacheLine);
  }

  private unloadCacheEntry(_: CacheDatum<GeoJSONFeatureCollection, RetryState> | undefined, cacheId: string) {
    delete this.cache[cacheId];
    this.clearPendingGeoJsonMeta(cacheId);
    this.lightningRequestMeta.delete(cacheId);
  }

  private normalizeLightningFormat(request: LightningListRequest<CoordinateSystem.WGS84>): string {
    return request.format ?? "json";
  }

  private isSameLightningRequestWindow(
    left: LightningListRequest<CoordinateSystem.WGS84>,
    right: LightningListRequest<CoordinateSystem.WGS84>,
  ): boolean {
    return (
      left.datetime.toMillis() === right.datetime.toMillis() &&
      left.timeDuration.as("milliseconds") === right.timeDuration.as("milliseconds") &&
      this.normalizeLightningFormat(left) === this.normalizeLightningFormat(right) &&
      !!left.enable_feature === !!right.enable_feature
    );
  }

  private isAreaContainingArea(
    container: Area<CoordinateSystem.WGS84>,
    inner: Area<CoordinateSystem.WGS84>,
    epsilon = 1e-9,
  ): boolean {
    return (
      container.west <= inner.west + epsilon &&
      container.east >= inner.east - epsilon &&
      container.south <= inner.south + epsilon &&
      container.north >= inner.north - epsilon
    );
  }

  private findLoadedLightningSuperset(
    request: LightningListRequest<CoordinateSystem.WGS84>,
    excludedCacheId: string,
    requestIsLive: boolean,
  ): LoadedCacheDatum<GeoJSONFeatureCollection> | null {
    // Reuse superset responses (same window/format, larger bbox) to prevent
    // cache churn and duplicate network requests while zooming in.
    for (const [candidateCacheId, candidateRequest] of this.lightningRequestMeta.entries()) {
      if (candidateCacheId === excludedCacheId) {
        continue;
      }
      if (!this.isSameLightningRequestWindow(candidateRequest, request)) {
        continue;
      }
      if (!this.isAreaContainingArea(candidateRequest.area, request.area)) {
        continue;
      }

      const candidateEntry = this.cache[candidateCacheId];
      if (!candidateEntry || candidateEntry.state !== CacheDatumState.Loaded) {
        continue;
      }
      if (requestIsLive && this.shouldRevalidateLightningCacheLine(candidateCacheId)) {
        continue;
      }

      this.markUsed(candidateEntry);
      return candidateEntry;
    }
    return null;
  }

  private pruneCacheEntries(protectedCacheIds: string[] = []) {
    if (protectedCacheIds.length === 0) {
      evictCacheEntriesBySize(this.cache, this.maxSize, this.delayEvictionUntilSize, this.unloadCacheEntry.bind(this));
      return;
    }

    // Temporarily remove protected entries from the eviction candidate set, then
    // restore them after pruning.
    const protectedEntries = new Map<string, CacheDatum<GeoJSONFeatureCollection, RetryState>>();
    for (const cacheId of protectedCacheIds) {
      const entry = this.cache[cacheId];
      if (entry) {
        protectedEntries.set(cacheId, entry);
        delete this.cache[cacheId];
      }
    }

    evictCacheEntriesBySize(this.cache, this.maxSize, this.delayEvictionUntilSize, this.unloadCacheEntry.bind(this));

    for (const [cacheId, entry] of protectedEntries.entries()) {
      this.cache[cacheId] = entry;
    }
  }

  private createGeoJsonCancelable(
    cacheId: string,
    cancel: () => void,
    requestUrls: string[],
    onCancel?: () => void,
  ): () => void {
    let canceled = false;
    const dedupUrls = Array.from(new Set(requestUrls)).filter((url) => typeof url === "string" && url.length > 0);

    return () => {
      if (canceled) {
        return;
      }
      canceled = true;
      cancel();
      onCancel?.();
      if (dedupUrls.length) {
        apiThreadPool.abortRequestByUrl(dedupUrls);
      }
      delete this.cache[cacheId];
      this.clearPendingGeoJsonMeta(cacheId);
      this.lightningRequestMeta.delete(cacheId);
    };
  }

  private buildGeoJsonAbortUrl(request: GenericGeoJsonRequest): string | null {
    if (!request.area) {
      return null;
    }

    const normalized: GenericGeoJsonRequest = {
      ...request,
      area: request.area,
    };
    const sameCrsArea = new Area(CoordinateSystem.WGS84, WORLD_BBOX);

    if (request.boundingBoxLimit && !request.boundingBoxLimit.equals(sameCrsArea)) {
      const gridLike = GenericGeoJsonRequest_toGridRequest(request);
      const intersectionModelBounds = intersectWeatherModelBoundingBox(gridLike, request.boundingBoxLimit);

      if (intersectionModelBounds.kind === "PartiallyOutOfBounds") {
        normalized.area = intersectionModelBounds.intersection.project(gridLike.area.crs);
      }
    }

    return MeteomaticsApiUrl.forGeoJsonEndpoint(normalized).toString();
  }

  /**
   * A method to return a cached data from cache.
   * It does not trigger a new request when the given request hasn't been emitted.
   * @returns
   * @param cacheId
   */
  peekCache(cacheId: string): AsyncResult<GeoJSONFeatureCollection> {
    const v = this.cache[cacheId];
    if (!v) {
      return createNotRequestedAsyncResult(emptyFeatureCollection);
    }
    switch (v.state) {
      case CacheDatumState.Pending: {
        const retry_ = v.payload;
        if (retry_.failureState !== FailureState.FailedTemporarily) {
          return createPendingAsyncResult(v.promise, v.cancel);
        }
        // since the current invocation moves the state from [Pending/FailedTemporarily] to [Pending/Queued]
        // mark it as such to prevent concurrent invocations from reentering the remaining function body
        retry_.failureState = undefined;
        return createPendingAsyncResult(v.promise, v.cancel);
      }
      case CacheDatumState.PermanentlyFailed:
        return createResolvedAsyncResult(emptyFeatureCollection);
      case CacheDatumState.Loaded:
        this.markUsed(v);
        return createResolvedAsyncResult(v.payload);
      default: {
        const _exhaustive: never = v;
        return _exhaustive;
      }
    }
  }

  private isLightningWindowLive(request: LightningListRequest<CoordinateSystem.WGS84>): boolean {
    const now = DateTime.utc();
    const requestRightEdge = request.datetime;
    // Treat near-now windows as live so they can be periodically refreshed,
    // while historical windows stay immutable and cache-friendly.
    return requestRightEdge >= now.minus(LIGHTNING_LIVE_OFFSET) && requestRightEdge <= now.plus(LIGHTNING_MAX_FUTURE);
  }

  private shouldRevalidateLightningCacheLine(cacheId: string): boolean {
    const entry = this.cache[cacheId];
    if (!entry || entry.state !== CacheDatumState.Loaded) {
      return false;
    }
    const ageMs = performance.now() - entry.requestDatetime;
    return ageMs >= LIGHTNING_LIVE_BUCKET_TTL_MS;
  }

  private shouldPreserveCacheEntryOnClear(cacheId: string): boolean {
    if (!cacheId.includes(LIGHTNING_LIST_PATH)) {
      return false;
    }
    const lightningRequest = this.lightningRequestMeta.get(cacheId);
    if (!lightningRequest) {
      return false;
    }
    // Keep only non-live lightning windows (older than ~10 minutes), matching previous clear behavior.
    return lightningRequest.datetime < DateTime.utc().minus(LIGHTNING_LIVE_OFFSET);
  }

  retrieveIsoLine(
    request: IsoLinesRequest,
    kind: IsoLinesRequestKind = IsoLinesRequestKind.IsoLines,
    opts?: PendingGeoJsonRequestOptions,
  ): AsyncResult<GeoJSONFeatureCollection> {
    const cacheId = generateIsoLinesLayerCacheId(request, kind);
    const retry_: RetryState | null = null;

    if (Object.hasOwn(this.cache, cacheId)) {
      if (this.cache[cacheId]?.state === CacheDatumState.Pending) {
        this.registerPendingGeoJsonMeta(cacheId, { area: request.area, owner: opts?.owner, path: request.path });
      }
      return this.peekCache(cacheId);
    }
    const modelBoundingBox = request.boundingBoxLimit || WORLD_BBOX;
    const retry = retry_ ?? createRetryState(modelBoundingBox);
    const abortUrls = [MeteomaticsApiUrl.forIsoLines(request).toString()];
    const fetchStarted = performance.now();
    const cancelablePromise = cancelable(
      apiThreadPool
        .getIsoLines(request, retry)
        .then((dataGeoJSON) => {
          const source = kind === IsoLinesRequestKind.IsoBands ? "isoband" : "isoline";
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source,
            kind,
          });
          const { requestDatetime } = this.cache[cacheId];
          const cacheData: LoadedCacheDatum<GeoJSONFeatureCollection> = createLoadedCacheDatum(
            dataGeoJSON,
            requestDatetime,
          );
          this.markUsed(cacheData);
          this.cache[cacheId] = cacheData;
          this.pruneCacheEntries();
          this.clearPendingGeoJsonMeta(cacheId);
          return dataGeoJSON;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          if (isAbortable(e)) {
            delete this.cache[cacheId];
            this.clearPendingGeoJsonMeta(cacheId);
            return Promise.reject(e);
          }
          this.clearPendingGeoJsonMeta(cacheId);
          return this.reactOnMeteomaticsApiError(e, retry, cacheId);
        }),
    );
    const cancel = this.createGeoJsonCancelable(cacheId, cancelablePromise.cancel, abortUrls);

    this.cache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    this.registerPendingGeoJsonMeta(cacheId, { area: request.area, owner: opts?.owner, path: request.path });
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  retrieveLightningList(
    request: LightningListRequest<CoordinateSystem.WGS84>,
    opts?: PendingGeoJsonRequestOptions,
  ): AsyncResult<GeoJSONFeatureCollection> {
    // Layer requests are canonicalized before keying/fetching, so tiny timeline
    // shifts (e.g. +1 minute auto-refresh) map to stable cache windows.
    const canonicalRequest = canonicalizeLightningRequestWindow(request);
    const apiRequest = toLightningApiAlignedRequest(canonicalRequest);
    const cacheId = generateLightningListLayerCacheId(canonicalRequest);
    const requestIsLive = this.isLightningWindowLive(canonicalRequest);
    const retry_: RetryState | null = null;

    if (Object.hasOwn(this.cache, cacheId)) {
      const entry = this.cache[cacheId];
      if (entry?.state === CacheDatumState.Pending) {
        this.registerPendingGeoJsonMeta(cacheId, {
          area: request.area,
          owner: opts?.owner,
          path: LIGHTNING_LIST_PATH,
        });
        return this.peekCache(cacheId);
      }
      if (!requestIsLive || !this.shouldRevalidateLightningCacheLine(cacheId)) {
        return this.peekCache(cacheId);
      }
      // Force one refresh cycle for live-edge requests after TTL expiry.
      delete this.cache[cacheId];
      this.clearPendingGeoJsonMeta(cacheId);
      this.lightningRequestMeta.delete(cacheId);
    }

    // Reuse already loaded superset windows to avoid zoom-triggered re-fetches.
    // This keeps network stable when users zoom into an area after loading a wider view.
    const loadedSuperset = this.findLoadedLightningSuperset(canonicalRequest, cacheId, requestIsLive);
    if (loadedSuperset) {
      return createResolvedAsyncResult(loadedSuperset.payload);
    }

    const retry = retry_ ?? createRetryState();
    const abortUrls = [MeteomaticsApiUrl.forLightningList(apiRequest).toString()];
    const fetchStarted = performance.now();
    const cancelablePromise = cancelable(
      apiThreadPool
        .getLightningList(apiRequest, retry)
        .then((dataGeoJSON) => {
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source: "lightning",
          });
          const { requestDatetime } = this.cache[cacheId];
          this.cache[cacheId] = createLoadedCacheDatum(dataGeoJSON, requestDatetime);
          this.markUsed(this.cache[cacheId] as LoadedCacheDatum<GeoJSONFeatureCollection>);
          // Avoid immediately evicting the just-loaded lightning aggregate for large windows.
          this.pruneCacheEntries([cacheId]);
          this.clearPendingGeoJsonMeta(cacheId);
          return dataGeoJSON;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          if (isAbortable(e)) {
            delete this.cache[cacheId];
            this.clearPendingGeoJsonMeta(cacheId);
            return Promise.reject(e);
          }
          if (e.kind === "TimeError") {
            const { requestDatetime } = this.cache[cacheId];
            this.cache[cacheId] = createLoadedCacheDatum(emptyFeatureCollection, requestDatetime);
            this.markUsed(this.cache[cacheId] as LoadedCacheDatum<GeoJSONFeatureCollection>);
            this.pruneCacheEntries([cacheId]);
            this.clearPendingGeoJsonMeta(cacheId);
            return Promise.resolve(emptyFeatureCollection);
          }
          this.clearPendingGeoJsonMeta(cacheId);
          return this.reactOnMeteomaticsApiError(e, retry, cacheId);
        }),
    );
    const cancel = this.createGeoJsonCancelable(cacheId, cancelablePromise.cancel, abortUrls);

    this.cache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    this.lightningRequestMeta.set(cacheId, canonicalRequest);
    this.registerPendingGeoJsonMeta(cacheId, {
      area: request.area,
      owner: opts?.owner,
      path: LIGHTNING_LIST_PATH,
    });
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  /**
   * Retrieves a generic GeoJSON endpoint request and stores it in cache.
   * Optional split/format hooks allow multi-request assembly before caching.
   */
  retrieveGeoJsonEndpoint(
    request: GenericGeoJsonRequest,
    opts?: {
      kind?: LayerKind;
      owner?: PendingGeoJsonOwner;
      split?: (req: GenericGeoJsonRequest) => GenericGeoJsonRequest[]; // optional
      format?: (fc: GeoJSONFeatureCollection) => GeoJSONFeatureCollection; // optional
    },
  ): AsyncResult<GeoJSONFeatureCollection> {
    const shouldUseTiled =
      !opts?.split &&
      !opts?.format &&
      (opts?.kind === LayerKindName.JetStreamsLayerDescription ||
        opts?.kind === LayerKindName.ThunderstormTrackingLayerDescription);
    if (shouldUseTiled) {
      return this.retrieveGeoJsonEndpointTiled(request, opts?.kind, opts?.owner);
    }
    const cacheId = generateGenericCacheId(request, opts?.kind);
    if (Object.hasOwn(this.cache, cacheId)) {
      if (this.cache[cacheId]?.state === CacheDatumState.Pending) {
        this.registerPendingGeoJsonMeta(cacheId, {
          area: request.area,
          kind: opts?.kind,
          owner: opts?.owner,
          path: request.path,
        });
      }
      return this.peekCache(cacheId);
    }

    const retry = createRetryState(request.boundingBoxLimit ?? WORLD_BBOX);
    const abortUrl = this.buildGeoJsonAbortUrl(request);
    const abortUrls = abortUrl ? [abortUrl] : [];

    const doFetch = async () => {
      const parts = opts?.split ? opts.split(request) : [request];
      const fcs = await Promise.all(parts.map((p) => apiThreadPool.getGeoJsonEndpoint(p, retry)));
      const merged = mergeFeatureCollections(fcs);
      return opts?.format ? opts.format(merged) : merged;
    };

    const fetchStarted = performance.now();
    const cancelablePromise = cancelable(
      doFetch()
        .then((fc) => {
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source: "geojson_endpoint",
            kind: opts?.kind,
          });
          const { requestDatetime } = this.cache[cacheId];
          this.cache[cacheId] = createLoadedCacheDatum(fc, requestDatetime);
          this.markUsed(this.cache[cacheId] as LoadedCacheDatum<GeoJSONFeatureCollection>);
          this.pruneCacheEntries();
          this.clearPendingGeoJsonMeta(cacheId);
          return fc;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          if (isAbortable(e)) {
            delete this.cache[cacheId];
            this.clearPendingGeoJsonMeta(cacheId);
            return Promise.reject(e);
          }
          this.clearPendingGeoJsonMeta(cacheId);
          return this.reactOnMeteomaticsApiError(e, retry, cacheId);
        }),
    );

    const cancel = this.createGeoJsonCancelable(cacheId, cancelablePromise.cancel, abortUrls);

    this.cache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    this.registerPendingGeoJsonMeta(cacheId, {
      area: request.area,
      kind: opts?.kind,
      owner: opts?.owner,
      path: request.path,
    });
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  /**
   * Peeks a GeoJSON endpoint cache entry without triggering network requests.
   * Mirrors retrieveGeoJsonEndpoint routing, including Jet Streams tiled aggregate IDs.
   */
  peekGeoJsonEndpoint(
    request: GenericGeoJsonRequest,
    opts?: {
      kind?: LayerKind;
    },
  ): AsyncResult<GeoJSONFeatureCollection> {
    if (
      opts?.kind === LayerKindName.JetStreamsLayerDescription ||
      opts?.kind === LayerKindName.ThunderstormTrackingLayerDescription
    ) {
      const tileWindow = this.resolveGeoJsonTileWindow(request, opts?.kind);
      if (tileWindow) {
        return this.peekCache(tileWindow.aggregateCacheId);
      }
    }

    const cacheId = generateGenericCacheId(request, opts?.kind);
    return this.peekCache(cacheId);
  }

  /**
   * Resolves the stable tile window and aggregate cache id for tiled GeoJSON endpoint requests.
   */
  private resolveGeoJsonTileWindow(
    request: GenericGeoJsonRequest,
    kind?: LayerKind,
  ): {
    tiles: Array<{ x: number; y: number; z: number; bbox: BBox }>;
    queryKey: string;
    aggregateCacheId: string;
  } | null {
    const area = request.area ?? WORLD_BBOX;
    const viewport: BBox = {
      west: area.west,
      east: area.east,
      south: area.south,
      north: area.north,
    };

    const tiles = getGeoJsonTilesForViewport(viewport, kind);
    if (tiles.length === 0) {
      return null;
    }

    const z = tiles[0].z;
    const xMin = Math.min(...tiles.map((t) => t.x));
    const xMax = Math.max(...tiles.map((t) => t.x));
    const yMin = Math.min(...tiles.map((t) => t.y));
    const yMax = Math.max(...tiles.map((t) => t.y));
    const queryKey = stableQueryKey(request.query as Record<string, unknown> | undefined);
    const aggregateCacheId = buildGeoJsonAggregateCacheId(request, z, xMin, xMax, yMin, yMax, queryKey, kind);

    return { tiles, queryKey, aggregateCacheId };
  }

  /**
   * Retrieves the tiled GeoJSON endpoint aggregate for Jet Streams.
   * Tile requests are cached individually; this returns/updates the aggregate cache entry.
   */
  private retrieveGeoJsonEndpointTiled(
    request: GenericGeoJsonRequest,
    kind?: LayerKind,
    owner?: PendingGeoJsonOwner,
  ): AsyncResult<GeoJSONFeatureCollection> {
    const tileConfig = getGeoJsonTileConfig(kind);
    const tileWindow = this.resolveGeoJsonTileWindow(request, kind);
    if (!tileWindow) {
      return this.retrieveGeoJsonEndpoint(request, {
        kind: kind,
        owner,
        split: (req) => [req],
      });
    }

    const { tiles, queryKey, aggregateCacheId } = tileWindow;

    if (Object.hasOwn(this.cache, aggregateCacheId)) {
      if (this.cache[aggregateCacheId]?.state === CacheDatumState.Pending) {
        this.registerPendingGeoJsonMeta(aggregateCacheId, {
          area: request.area,
          kind,
          owner,
          path: request.path,
        });
      }
      return this.peekCache(aggregateCacheId);
    }

    const retry = createRetryState(request.boundingBoxLimit ?? WORLD_BBOX);
    const aggregateAbortUrls = new Set<string>();
    const tileResults = tiles.map(({ x, y, bbox }) => {
      const buffered = bufferBBox(bbox, GEOJSON_TILE_BUFFER_RATIO);
      const tileArea = new Area(CoordinateSystem.WGS84, {
        west: buffered.west,
        south: buffered.south,
        east: buffered.east,
        north: buffered.north,
      });
      const subRequest: GenericGeoJsonRequest = {
        ...request,
        area: tileArea,
      };
      const tileAbortUrl = this.buildGeoJsonAbortUrl(subRequest);
      if (tileAbortUrl) {
        aggregateAbortUrls.add(tileAbortUrl);
      }
      const tileCacheId = buildGeoJsonTileCacheId(request, tileConfig.zoom, x, y, queryKey, kind);
      return this.retrieveGeoJsonTile(tileCacheId, subRequest, retry, kind);
    });

    const doFetch = async () => {
      const fcs = await Promise.all(tileResults.map((result) => result.asynchronous));
      const dedupKeyFn = kind === LayerKindName.JetStreamsLayerDescription ? jetStreamsDedupKey : thunderstormDedupKey;
      return mergeFeatureCollectionsDedup(fcs, dedupKeyFn);
    };

    const fetchStarted = performance.now();
    const cancelablePromise = cancelable(
      doFetch()
        .then((fc) => {
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source: "geojson_endpoint",
            kind,
          });
          const { requestDatetime } = this.cache[aggregateCacheId];
          this.cache[aggregateCacheId] = createLoadedCacheDatum(fc, requestDatetime);
          this.markUsed(this.cache[aggregateCacheId] as LoadedCacheDatum<GeoJSONFeatureCollection>);
          this.pruneCacheEntries();
          this.clearPendingGeoJsonMeta(aggregateCacheId);
          return fc;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          if (isAbortable(e)) {
            delete this.cache[aggregateCacheId];
            this.clearPendingGeoJsonMeta(aggregateCacheId);
            return Promise.reject(e);
          }
          this.clearPendingGeoJsonMeta(aggregateCacheId);
          return this.reactOnMeteomaticsApiError(e, retry, aggregateCacheId);
        }),
    );

    const cancel = this.createGeoJsonCancelable(
      aggregateCacheId,
      cancelablePromise.cancel,
      Array.from(aggregateAbortUrls),
    );

    this.cache[aggregateCacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    this.registerPendingGeoJsonMeta(aggregateCacheId, {
      area: request.area,
      kind,
      owner,
      path: request.path,
    });
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  /**
   * Retrieves a single tile-sized GeoJSON request and stores it under a tile cache id.
   */
  private retrieveGeoJsonTile(
    cacheId: string,
    request: GenericGeoJsonRequest,
    retry: RetryState,
    kind?: string,
  ): AsyncResult<GeoJSONFeatureCollection> {
    if (Object.hasOwn(this.cache, cacheId)) {
      return this.peekCache(cacheId);
    }

    const abortUrl = this.buildGeoJsonAbortUrl(request);
    const abortUrls = abortUrl ? [abortUrl] : [];
    const fetchStarted = performance.now();
    const cancelablePromise = cancelable(
      apiThreadPool
        .getGeoJsonEndpoint(request, retry)
        .then((fc) => {
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source: "geojson_endpoint",
            kind,
          });
          const { requestDatetime } = this.cache[cacheId];
          this.cache[cacheId] = createLoadedCacheDatum(fc, requestDatetime);
          this.markUsed(this.cache[cacheId] as LoadedCacheDatum<GeoJSONFeatureCollection>);
          this.pruneCacheEntries();
          this.clearPendingGeoJsonMeta(cacheId);
          return fc;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          if (isAbortable(e)) {
            delete this.cache[cacheId];
            this.clearPendingGeoJsonMeta(cacheId);
            return Promise.reject(e);
          }
          this.clearPendingGeoJsonMeta(cacheId);
          return this.reactOnMeteomaticsApiError(e, retry, cacheId);
        }),
    );

    const cancel = this.createGeoJsonCancelable(cacheId, cancelablePromise.cancel, abortUrls);

    this.cache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    this.registerPendingGeoJsonMeta(cacheId, { kind, path: request.path });
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  /**
   * Retrieves the XML from a WFS interface and converts it into GeoJSON.
   * This implementation splits the request into visible XYZ tiles at a fixed zoom level,
   * creates per-tile subrequests and per-tile cache entries, fetches only missing tiles, and merges the results.
   */
  retrieveWfs(
    request: WfsRequest<CoordinateSystem.WGS84>,
    geoJSONFormatter?: (featureCollection: GeoJSONFeatureCollection) => GeoJSONFeatureCollection,
    opts?: PendingGeoJsonRequestOptions,
  ) {
    // We split the incoming WFS request by visible XYZ tiles at a fixed zoom level.
    const TILE_ZOOM = WFS_TILE_ZOOM;

    // Compute visible tiles and intersect with any modelBoundingBox limits
    const modelArea = request.boundingBoxLimit || WORLD_BBOX;
    // const modelBBox: BBox = {
    //   west: modelArea.west,
    //   east: modelArea.east,
    //   south: modelArea.south,
    //   north: modelArea.north,
    // };

    // Determine visible tiles at Z before clipping (for aggregate cache id)
    const tileWindow = getWfsTileWindow(request, TILE_ZOOM);
    if (!tileWindow) {
      return createResolvedAsyncResult(emptyFeatureCollection);
    }
    const { visibleTiles, aggregateCacheId } = tileWindow;

    // Return immediately if an aggregate for these tiles is already known
    if (Object.hasOwn(this.cache, aggregateCacheId)) {
      if (this.cache[aggregateCacheId]?.state === CacheDatumState.Pending) {
        this.registerPendingGeoJsonMeta(aggregateCacheId, {
          area: request.area,
          owner: opts?.owner,
          path: request.requestType,
        });
      }
      return this.peekCache(aggregateCacheId);
    }

    // --- TILE CLIPPING ---
    // We want FULL tiles (e.g., for z=1 the 2x2 world tiles), not viewport-cropped pieces.
    // Therefore, we DISABLE clipping to the viewport/modelBBox here and use each tile's full bbox.
    // If you need clipping later, re-enable the block below.
    //
    // Disabled (original):
    // const tiles = visibleTiles
    //   .map((t) => {
    //     const clippedToViewport = intersectBBox(t.bbox, viewport);
    //     if (!clippedToViewport) return null;
    //     const clipped = intersectBBox(clippedToViewport, modelBBox);
    //     if (!clipped) return null;
    //     return { ...t, clip: clipped };
    //   })
    //   .filter((t): t is { x: number; y: number; z: number; bbox: BBox; clip: BBox } => !!t);
    //
    // Active (no clipping): use full tile bbox as 'clip'
    const tiles = visibleTiles.map((t) => ({ ...t, clip: t.bbox }));

    // Generate sub-requests per tile
    const subRequests = tiles.map(({ x, y, z, clip }) => {
      const area = new Area(CoordinateSystem.WGS84, {
        west: clip.west,
        south: clip.south,
        east: clip.east,
        north: clip.north,
      });
      const subReq: WfsRequest<CoordinateSystem.WGS84> = {
        ...request,
        area,
      };
      // Different area implies different cache id; we can also append a stable tile key if desired.
      return { subReq, key: `x${x}-y${y}-z${z}` };
    });

    // For parity with other splitters (e.g., lightning), we execute (and cache) each sub-request,
    // then merge the resulting FeatureCollections.
    const listAsync = subRequests.map(({ subReq, key }) => {
      const subCacheId = buildWfsTileCacheId(request, key);

      if (Object.hasOwn(this.cache, subCacheId)) {
        return this.peekCache(subCacheId);
      }

      const retry_: RetryState | null = null;
      const subRetry = retry_ ?? createRetryState(request.boundingBoxLimit || WORLD_BBOX);
      const subAbortUrls = [MeteomaticsApiUrl.forWfs(subReq).toString()];
      const subCancelablePromise = cancelable(
        apiThreadPool
          .getWfs(subReq, subRetry)
          .then((data) => {
            const result = geoJSONFormatter ? geoJSONFormatter(data) : data;
            const { requestDatetime } = this.cache[subCacheId];
            const cacheData: LoadedCacheDatum<GeoJSONFeatureCollection> = createLoadedCacheDatum(
              result,
              requestDatetime,
            );
            this.markUsed(cacheData);
            this.cache[subCacheId] = cacheData;
            this.pruneCacheEntries();
            return result;
          })
          .catch((e: MeteomaticsApiError | Abort) => {
            if (isAbortable(e)) {
              delete this.cache[subCacheId];
              return Promise.reject(e);
            }
            return this.reactOnMeteomaticsApiError(e as MeteomaticsApiError, subRetry, subCacheId);
          }),
      );
      const subCancel = this.createGeoJsonCancelable(subCacheId, subCancelablePromise.cancel, subAbortUrls);

      this.cache[subCacheId] = {
        requestDatetime: performance.now(),
        state: CacheDatumState.Pending,
        promise: subCancelablePromise.promise,
        cancel: subCancel,
        payload: subRetry,
      };
      return createPendingAsyncResult(subCancelablePromise.promise, subCancel);
    });

    // Aggregate promise over all tile requests
    const fetchStarted = performance.now();
    const aggregateCancelablePromise = cancelable(
      Promise.all(listAsync.map((ar) => ar.asynchronous))
        .then((parts) => {
          const valid = parts.filter(Boolean) as GeoJSONFeatureCollection[];
          const merged = valid.length ? mergeFeatureCollections(valid) : emptyFeatureCollection;
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source: "wfs",
          });
          // store aggregate result in cache
          const { requestDatetime } = this.cache[aggregateCacheId] || { requestDatetime: performance.now() };
          this.cache[aggregateCacheId] = createLoadedCacheDatum(merged, requestDatetime);
          this.markUsed(this.cache[aggregateCacheId] as LoadedCacheDatum<GeoJSONFeatureCollection>);
          this.pruneCacheEntries();
          this.clearPendingGeoJsonMeta(aggregateCacheId);
          return merged;
        })
        .catch((e) => {
          if (!Object.hasOwn(this.cache, aggregateCacheId)) {
            return Promise.reject(e);
          }

          // On error, mark aggregate as permanently failed (or store empty to avoid spinners)
          this.cache[aggregateCacheId] = {
            requestDatetime: performance.now(),
            state: CacheDatumState.PermanentlyFailed,
            payload: null,
          };
          this.clearPendingGeoJsonMeta(aggregateCacheId);
          return Promise.reject(e);
        }),
    );
    const aggregateCancel = this.createGeoJsonCancelable(
      aggregateCacheId,
      aggregateCancelablePromise.cancel,
      [],
      () => {
        for (const subRequest of listAsync) {
          subRequest.cancel?.();
        }
      },
    );

    // mark aggregate as pending before the requests finish so concurrent calls can reuse it
    const aggregateRetry = createRetryState(modelArea);
    this.cache[aggregateCacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise: aggregateCancelablePromise.promise,
      cancel: aggregateCancel,
      payload: aggregateRetry,
    };
    this.registerPendingGeoJsonMeta(aggregateCacheId, {
      area: request.area,
      owner: opts?.owner,
      path: request.requestType,
    });

    return createPendingAsyncResult(aggregateCancelablePromise.promise, aggregateCancel);
  }

  retrieveGrid(
    request: GridRequest<CoordinateSystem.WGS84>,
    kind: GridRequestKind = GridRequestKind.Raw,
    geoJSONFormatter?: (jsonResponse: JSONResponseBody) => GeoJSONFeatureCollection,
    opts?: PendingGeoJsonRequestOptions,
  ): AsyncResult<GeoJSONFeatureCollection> {
    const cacheId = generateGridLayerCacheId(request, kind);
    if (Object.hasOwn(this.cache, cacheId)) {
      if (this.cache[cacheId]?.state === CacheDatumState.Pending) {
        this.registerPendingGeoJsonMeta(cacheId, { area: request.area, owner: opts?.owner });
      }
      return this.peekCache(cacheId);
    }

    const retry_: RetryState | null = null;
    const modelBoundingBox = request.boundingBoxLimit || WORLD_BBOX;
    const retry = retry_ ?? createRetryState(modelBoundingBox);
    const abortUrls = [MeteomaticsApiUrl.forGrid({ format: "json" }, request).toString()];
    const fetchStarted = performance.now();
    const cancelablePromise = cancelable(
      apiThreadPool
        .getJSON(request, retry)
        .then((data) => {
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source: "grid",
            kind,
          });
          let result: GeoJSONFeatureCollection;
          switch (kind) {
            // TODO-StationFeature:
            // These JSON transformation function could be passed as a callback from LayerImpl class so we dont't have a huge switch statement?
            case GridRequestKind.Raw:
              if (geoJSONFormatter) {
                result = geoJSONFormatter(data);
              } else {
                result = createRawGeoJson(data);
              }
              break;
            case GridRequestKind.Symbol:
              result = createWeatherSymbolGeoJson(data);
              break;
            case GridRequestKind.WeatherCode:
              result = createWeatherCodeGeoJson(data);
              break;
            case GridRequestKind.Barbs:
              result = createBarbsGeoJson(data);
              break;
            default: {
              const _exhaustive: never = kind;
              return _exhaustive;
            }
          }
          const { requestDatetime } = this.cache[cacheId];
          const cacheData: LoadedCacheDatum<GeoJSONFeatureCollection> = createLoadedCacheDatum(result, requestDatetime);
          this.markUsed(cacheData);
          this.cache[cacheId] = cacheData;
          this.pruneCacheEntries();
          this.clearPendingGeoJsonMeta(cacheId);
          return result;
        })
        .catch((e: MeteomaticsApiError | Abort) => {
          if (isAbortable(e)) {
            delete this.cache[cacheId];
            this.clearPendingGeoJsonMeta(cacheId);
            return Promise.reject(e);
          }
          this.clearPendingGeoJsonMeta(cacheId);
          return this.reactOnMeteomaticsApiError(e, retry, cacheId);
        }),
    );
    const cancel = this.createGeoJsonCancelable(cacheId, cancelablePromise.cancel, abortUrls);

    this.cache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    this.registerPendingGeoJsonMeta(cacheId, { area: request.area, owner: opts?.owner });
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  retrieveGeoJson(
    requestUrl: string,
    extern = false,
    opts?: PendingGeoJsonRequestOptions & { area?: Area<CoordinateSystem.WGS84> },
  ): AsyncResult<GeoJSONFeatureCollection> {
    const cacheId = `geojson_${requestUrl.toString()}`;

    let retry_: RetryState | null = null;
    if (Object.hasOwn(this.cache, cacheId)) {
      const v = this.cache[cacheId];
      if (v.state === CacheDatumState.Pending) {
        this.registerPendingGeoJsonMeta(cacheId, { area: opts?.area, owner: opts?.owner });
      }
      switch (v.state) {
        case CacheDatumState.Pending:
          retry_ = v.payload;
          if (retry_.failureState !== FailureState.FailedTemporarily) {
            return createPendingAsyncResult(v.promise, v.cancel);
          }
          // since the current invocation moves the state from [Pending/FailedTemporarily] to [Pending/Queued]
          // mark it as such to prevent concurrent invocations from reentering the remaining function body
          retry_.failureState = undefined;
          break;
        case CacheDatumState.PermanentlyFailed:
          // TODO: store and forward failure reason?
          // fill with empty
          return createResolvedAsyncResult(emptyFeatureCollection);
        // return createPermanentlyFailedAsyncResult(v.payload);
        case CacheDatumState.Loaded:
          return createResolvedAsyncResult(v.payload);
        default: {
          const _exhaustive: never = v;
          return _exhaustive;
        }
      }
    }

    const modelBoundingBox = new Area(CoordinateSystem.WGS84, {
      east: 180,
      west: -180,
      north: 90,
      south: -90,
    });
    const retry = retry_ ?? createRetryState(modelBoundingBox);

    const url = extern ? getProxyUrl(requestUrl) : requestUrl;

    const abortController = new AbortController();
    const fetchStarted = performance.now();
    const cancelablePromise = cancelable(
      fetch(url, { signal: abortController.signal })
        .then((data) => {
          return data.json();
        })
        .then((result) => {
          recordLayerFetchMs({
            durationMs: performance.now() - fetchStarted,
            source: "geojson",
            mode: extern ? "external" : "internal",
          });
          const { requestDatetime } = this.cache[cacheId];
          const cacheData: LoadedCacheDatum<GeoJSONFeatureCollection> = createLoadedCacheDatum(result, requestDatetime);
          this.markUsed(cacheData);
          this.cache[cacheId] = cacheData;
          this.pruneCacheEntries();
          this.clearPendingGeoJsonMeta(cacheId);
          return result;
        })
        .catch((e) => {
          if (!Object.hasOwn(this.cache, cacheId)) {
            return Promise.reject(e);
          }

          this.cache[cacheId] = {
            requestDatetime: performance.now(),
            state: CacheDatumState.PermanentlyFailed,
            payload: null,
          };
          this.clearPendingGeoJsonMeta(cacheId);

          return Promise.reject(e);
        }),
    );
    const cancel = this.createGeoJsonCancelable(cacheId, cancelablePromise.cancel, [], () => {
      abortController.abort();
    });

    this.cache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise: cancelablePromise.promise,
      cancel,
      payload: retry,
    };
    this.registerPendingGeoJsonMeta(cacheId, { area: opts?.area, owner: opts?.owner });
    return createPendingAsyncResult(cancelablePromise.promise, cancel);
  }

  reactOnMeteomaticsApiError(e: MeteomaticsApiError, retry: RetryState, cacheId: string): Promise<never> {
    // Todo: Make different reactions on Errors, currently always return a CacheDatumState.PermanentlyFailed
    // ToDo: Handle Out of bounds.
    switch (e.kind) {
      case "BoundsError":
      case "TimeError":
      case "ComputationTimeoutError":
      case "AuthError":
      case "UnknownError":
      case "UnknownParameterError":
      case "MissingDataError": {
        const { requestDatetime } = this.cache[cacheId];
        const cacheData: PermanentlyFailedCacheDatum<null> = {
          requestDatetime,
          state: CacheDatumState.PermanentlyFailed,
          payload: null,
        };
        this.cache[cacheId] = cacheData;
        // retry.failureState = FailureState.FailedPermanently;
        return Promise.reject(e);
      }
      default: {
        const _exhaustive: never = e;
        return _exhaustive;
      }
    }
    // Still here for retry functionality
    // Retry would be something for boundsError -> cropping bbox and retry ...
    // retry.failedAttempts++; // accumulated failures for the current tile only
    // retry.failureState = FailureState.FailedTemporarily;
    // if (retry.failedAttempts > NETWORK_EXPONENTIAL_BACKOFF_MAX_RETRIES) {
    //   retry.failureState = FailureState.FailedPermanently;
    //   return Promise.reject(e);
    // }
    //
    // return Promise.reject(e);
  }
}
